import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const stops = [
  {
    step: 1,
    title: 'Roll Out Like a VIP',
    description: 'We scoop you up straight from your Vegas hotel — no stress, no waiting. From the jump, you\'re riding like a star. Slide into plush seats inside a fully loaded SUV, conversion van, or limo bus. Free WiFi, surround-sound movies, and room to kick back while the desert rolls by. Breakfast snacks and cold drinks? Already handled.',
    image: 'https://images.pexels.com/photos/1899236/pexels-photo-1899236.jpeg',
    imageAlt: 'Scenic desert highway from Las Vegas to California at sunrise',
  },
  {
    step: 2,
    title: 'Santa Monica: Beach Mode On',
    description: 'Swap desert heat for ocean breeze. Hit the pier, chase thrills on the rides, or go full chill with your feet in the Pacific. This is your California moment — own it.',
    image: 'https://images.pexels.com/photos/804115/pexels-photo-804115.jpeg',
    imageAlt: 'Santa Monica Pier at sunset with colorful sky over the Pacific Ocean',
  },
  {
    step: 3,
    title: 'Lights, Camera... Hollywood',
    description: 'Pull up to Warner Bros. Studios and grab those behind-the-scenes shots where movie magic actually happens. One of the most iconic production studios on the planet — and you\'re standing right in front of it.',
    image: 'https://images.pexels.com/photos/36148684/pexels-photo-36148684.jpeg',
    imageAlt: 'Warner Bros Studios water tower in Burbank California at sunset',
  },
  {
    step: 4,
    title: 'Fuel Up Like a Star',
    description: 'Time to eat. Choose your vibe — grilled, fresh, bold, or indulgent. From tacos and burritos to seafood and power bowls, this lunch hits different. Sit-down restaurant dining, not fast food. Vegetarian and Halal options available.',
    image: null,
    imageAlt: '',
  },
  {
    step: 5,
    title: 'Cruise Beverly Hills in Style',
    description: 'Glide past insane mansions, legendary hangouts, and film locations along Sunset Boulevard. Yeah, this is that lifestyle. Celebrity homes, iconic landmarks, and the energy that defines Hollywood.',
    image: 'https://images.unsplash.com/photo-1599565508598-50662247a557',
    imageAlt: 'Luxury Beverly Hills celebrity mansion with tall palm trees',
  },
  {
    step: 6,
    title: 'Rodeo Drive Flex',
    description: 'Step onto Rodeo Drive and soak in the luxury. Window shop the world\'s most exclusive boutiques, people watch, and snap pics like you belong there — because right now, you do.',
    image: 'https://static.prod-images.emergentagent.com/jobs/e17238f4-6670-41c0-9f2d-00e20837d10a/images/fafec82a2924312616767d0f5acaab7508ef26c05908f15f5030b099cbc74c79.png',
    imageAlt: 'Beverly Hills Rodeo Drive luxury shopping district with palm trees',
  },
  {
    step: 7,
    title: 'Hollywood Walk of Fame Takeover',
    description: 'Hit Hollywood Boulevard, find your favorite stars among 2,700+ names, and stand where the biggest names in entertainment made history. TCL Chinese Theatre, celebrity handprints, Hollywood Highland Center — it\'s all here.',
    image: 'https://images.pexels.com/photos/4700359/pexels-photo-4700359.jpeg',
    imageAlt: 'Hollywood Walk of Fame stars illuminated at night showcasing celebrity names',
  },
  {
    step: 8,
    title: 'Hollywood Sign: The Grand Finale',
    description: 'We take you up into the hills for that unreal, up-close view of the Hollywood Sign. Skyline, sunsets, city vibes — it\'s the shot everyone wants, and you\'re getting it. One day. Zero regrets. This is the kind of trip you talk about long after it\'s over.',
    image: 'https://images.pexels.com/photos/2695679/pexels-photo-2695679.jpeg',
    imageAlt: 'Hollywood Sign on the hillside with Los Angeles skyline view',
  },
];

const Itinerary = () => {
  return (
    <section id="itinerary" className="section-padding bg-[#0A0A0A]" data-testid="itinerary-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <p className="font-caveat text-xl sm:text-2xl text-[#FFD600] mb-2">Your Day, Planned to Perfection</p>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase text-white">
            Vegas to Hollywood Tour Itinerary
          </h2>
          <p className="font-outfit text-base sm:text-lg text-white/70 max-w-2xl mx-auto mt-4">
            One epic day. Two iconic cities. A full-throttle sightseeing adventure from Las Vegas to Hollywood with breakfast, lunch, and every iconic stop included.
          </p>
        </motion.div>

        <div className="relative">
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#FF007A] via-[#FFD600] to-[#00E5FF]" />

          {stops.map((stop, i) => {
            const isLeft = i % 2 === 0;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className={`relative flex items-start gap-4 md:gap-8 mb-10 md:mb-14 ${
                  isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                } flex-row`}
                data-testid={`itinerary-stop-${stop.step}`}
              >
                <div className="absolute left-6 md:left-1/2 w-8 h-8 -translate-x-1/2 rounded-full bg-[#FF007A] border-2 border-white flex items-center justify-center z-10">
                  <span className="font-bebas text-white text-sm">{stop.step}</span>
                </div>

                <div className={`ml-14 md:ml-0 md:w-1/2 ${isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 sm:p-6">
                    <div className={`flex items-center gap-2 mb-2 ${isLeft ? 'md:justify-end' : 'md:justify-start'}`}>
                      <Clock className="w-4 h-4 text-[#FFD600]" />
                      <span className="font-outfit text-xs uppercase tracking-widest text-[#FFD600] font-bold">Stop {stop.step}</span>
                    </div>
                    <h3 className="font-bebas text-xl sm:text-2xl tracking-tight uppercase text-white mb-2">
                      {stop.title}
                    </h3>
                    <p className="font-outfit text-sm sm:text-base text-white/70 leading-relaxed">
                      {stop.description}
                    </p>
                    {stop.image && (
                      <div className="mt-4 rounded-lg overflow-hidden border border-white/10 img-zoom">
                        <img
                          src={stop.image}
                          alt={stop.imageAlt}
                          className="w-full h-40 sm:h-48 object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="hidden md:block md:w-1/2" />
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-8"
        >
          <a href="#booking" className="btn-primary px-8 py-4 text-base" data-testid="itinerary-book-cta">
            BOOK THIS TOUR - $215/PERSON
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Itinerary;
